import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { setFilter } from "../../redux/actions/filterActions";

/**
 * Props:
 * - showRadio (boolean): If true, show radio buttons with color names.
 *   If false, show only clickable color swatches (no labels).
 */
const ColorFilter = ({ showRadio = false }) => {
  const dispatch = useDispatch();
  const {
    loading: optionsLoading,
    data: filterOptions = {},
    error: optionsError,
  } = useSelector((state) => state.filterOptions) || {};
  const { colors = [] } = useSelector((state) => state.filters) || {};
  const colorOptions = filterOptions.colors || [];

  if (optionsError) return <p>Error loading colors: {optionsError}</p>;
  if (optionsLoading) return <p>Loading colors…</p>;

  // Handler for radio or swatch selection
  const handleSelect = (name) => {
    // Only one color can be selected at a time for radio, else toggle for swatch
    if (showRadio) {
      dispatch(setFilter("colors", [name]));
    } else {
      const updated = colors.includes(name)
        ? colors.filter((c) => c !== name)
        : [...colors, name];
      dispatch(setFilter("colors", updated));
    }
  };

  return (
    <div className="my-4">
      <h6 className="font-semibold lg:text-lg mb-2">Colors</h6>
      <div className={`flex flex-wrap gap-2 ${showRadio ? "flex-col" : ""}`}>
        {[
          ...new Map(
            colorOptions
              .filter(
                (c) => c.color_name && c.color_name.toLowerCase() !== "same"
              )
              .map((item) => [item.shade?.toLowerCase(), item])
          ).entries(),
        ].map(([shade, colorObj]) => {
          const name = colorObj.color_name;
          const colorId = colorObj.id;
          const shadeImg = colorObj.shade_img;
          const isSelected = colors.includes(colorId);

          if (showRadio) {
            return (
              <label
                key={colorObj.id}
                className="inline-flex items-center cursor-pointer gap-2"
              >
                <input
                  type="radio"
                  name="color-radio"
                  value={colorId}
                  checked={colors.length === 1 && colors[0] === colorId}
                  onChange={() => handleSelect(colorId)}
                  className="accent-teal-700"
                />
                {shadeImg ? (
                  <img
                    loading="lazy"
                    src={shadeImg}
                    alt={shade}
                    title={name}
                    className="w-5 h-5 rounded-full border-2 border-gray-300 object-cover"
                  />
                ) : (
                  <span
                    className="w-5 h-5 rounded-full border-2 border-gray-300"
                    style={{ backgroundColor: shade }}
                    title={name}
                  ></span>
                )}
                <span>{name}</span>
              </label>
            );
          } else {
            if (shadeImg) {
              return (
                <img
                  loading="lazy"
                  key={colorObj.id}
                  src={shadeImg}
                  alt={shade}
                  title={name}
                  onClick={() => handleSelect(name)}
                  className={`w-8 h-8 rounded-full border-2 object-cover cursor-pointer transition
                    ${
                      isSelected
                        ? "ring-2 ring-offset-1 ring-black border-black"
                        : "border-gray-300"
                    }`}
                />
              );
            } else {
              return (
                <button
                  key={colorObj.id}
                  type="button"
                  onClick={() => handleSelect(name)}
                  title={name}
                  className={`w-8 h-8 rounded-full border-2 transition
                    ${
                      isSelected
                        ? "ring-2 ring-offset-1 ring-black border-black"
                        : "border-gray-300"
                    }`}
                  aria-label={name}
                  style={{
                    outline: "none",
                    backgroundColor: shade,
                  }}
                />
              );
            }
          }
        })}
      </div>
    </div>
  );
};

export default ColorFilter;
